import { SellingZoneGraphItem } from '@substratahq/selling-zone/typing/base';
import { Reducer } from 'redux';
import {
  CLEAR_DETAILED_ANALYSIS,
  GET_DETAILED_ANALYSIS,
  GET_ENGAGEMENTS,
  REMOVE_THREAD,
  RENAME_ACCOUNT,
  RESET_ENG_PAGE_NUMBER,
  SET_CURRENT_BRANCH,
  SET_CURRENT_DEAL,
  SET_CURRENT_EMAIL,
  SET_CURRENT_THREAD,
  SET_DATA_SORT_CASE,
  SET_GRAPH_ITEMS,
  SET_LOADING_DETAILED_ANALYSIS,
  SET_LOADING_LIST,
  SET_REMOVE_THREAD_STATUS,
  SET_SELECTED_GRAPH_ITEM,
  UPDATE_ACCOUNT_INFO,
  UPDATE_CRM_DATA,
  UPDATE_CURRENT_EMAIL,
} from './deals.actions';
import {
  DealsActions,
  DealsForTable,
  DealsTableRow,
  DetailedAnalysis,
  DetailedAnalysisEmail,
  EmailBranch,
  EngagementsForTable,
  EngagementTableRow,
  RemoveThreadStatus,
  TableDataSortCase,
  TableRowAccount,
} from './deals.types';

export interface DealsState {
  deals: DealsForTable | null;
  engagements: EngagementsForTable | null;
  dealsOpenedFirts: boolean;
  dealsNoData: boolean;
  neverAnalyzed: boolean;
  currentDeal: EngagementTableRow | null;
  detailedAnalysis: DetailedAnalysis | null;
  isLoading: {
    deals: boolean;
    deatiledAnalysis: boolean;
  };
  selectedThread?: DealsTableRow | null;
  selectedBranch: EmailBranch | null;
  selectedEmail?: DetailedAnalysisEmail | null;
  graphItems: SellingZoneGraphItem[];
  selectedGraphItem?: SellingZoneGraphItem | null;
  removeThreadStatus?: RemoveThreadStatus;
  requestedPageNumber: number;
  dataSortCase: TableDataSortCase;
  requestedEngsQty: number;
}

export const initialState: DealsState = {
  deals: null,
  engagements: null,
  dealsOpenedFirts: false,
  dealsNoData: false,
  neverAnalyzed: false,
  currentDeal: null,
  detailedAnalysis: null,
  isLoading: {
    deals: false,
    deatiledAnalysis: false,
  },
  selectedThread: null,
  selectedBranch: null,
  selectedEmail: null,
  graphItems: [],
  selectedGraphItem: null,
  removeThreadStatus: null,
  requestedPageNumber: 1,
  dataSortCase: '1-9',
  requestedEngsQty: 10,
};

const updateAccInfo = (
  engagements: EngagementsForTable | null,
  payload: TableRowAccount
): EngagementsForTable | null => {
  let result = null;

  if (engagements && engagements.results?.length && payload) {
    result = {
      ...engagements,
      results: engagements.results.map((eng) =>
        payload.accountId === eng.account.accountId
          ? { ...eng, account: { ...eng.account, name: payload.name } }
          : eng
      ),
    };
  }

  return result;
};

const sentCurrentDeal = (
  engagements: EngagementsForTable | null,
  payload: EngagementTableRow | null
) => {
  let selectedDeal = null;

  if (engagements && engagements.results?.length && payload) {
    selectedDeal =
      engagements.results.find(
        (el) => el.engagementId === payload.engagementId
      ) || null;
  }

  return selectedDeal;
};

const updateEngagementsAccountInfo = (
  engagements: EngagementsForTable,
  payload: TableRowAccount
) => {
  let result = engagements;

  if (engagements && engagements?.results?.length > 0 && payload) {
    const updatedEngagements: EngagementsForTable = {
      ...engagements,
      results: engagements.results.map((el) => {
        let elementToReturn = el;

        if (el.account.accountId === payload.accountId) {
          elementToReturn = {
            ...el,
            account: {
              ...payload,
              isUpdated: 'UPDATED',
              accountId: payload.accountId,
              subrowQty: el.subrows?.length,
            },
          };
        }

        return elementToReturn;
      }),
    };

    result = updatedEngagements;
  }

  return result;
};

export const dealsReducer: Reducer<DealsState, DealsActions> = (
  state: DealsState = initialState,
  action: DealsActions
): DealsState => {
  switch (action.type) {
    case GET_ENGAGEMENTS:
      return {
        ...state,
        engagements: action.payload.engagements,
        requestedPageNumber: action.payload.pagenumber,
        dealsOpenedFirts:
          state.dealsOpenedFirts || state.engagements?.results.length
            ? initialState.dealsOpenedFirts
            : true,
        dealsNoData: !action.payload.engagements.results.length,
        neverAnalyzed:
          typeof action.payload.engagements.analyzerUsedOnce === 'boolean'
            ? !action.payload.engagements.analyzerUsedOnce
            : initialState.neverAnalyzed,
      };

    case UPDATE_ACCOUNT_INFO:
      return {
        ...state,
        engagements:
          state.engagements && action.payload
            ? updateEngagementsAccountInfo(state.engagements, action.payload)
            : state.engagements,
      };

    case SET_DATA_SORT_CASE:
      return {
        ...state,
        dataSortCase: action.payload,
      };

    case RESET_ENG_PAGE_NUMBER:
      return {
        ...state,
        requestedPageNumber: 1,
      };

    case SET_LOADING_LIST:
      return {
        ...state,
        isLoading: {
          ...state.isLoading,
          deals: action.payload,
        },
      };

    case SET_CURRENT_DEAL:
      return {
        ...state,
        currentDeal: sentCurrentDeal(state.engagements, action.payload),
      };

    case REMOVE_THREAD:
      return {
        ...state,
        deals: action.payload,
      };

    case SET_REMOVE_THREAD_STATUS:
      return {
        ...state,
        removeThreadStatus: action.payload,
      };

    case RENAME_ACCOUNT:
      return {
        ...state,
        engagements: updateAccInfo(state.engagements, action.payload),
      };

    case GET_DETAILED_ANALYSIS:
      return {
        ...state,
        detailedAnalysis: action.payload,
      };

    case SET_LOADING_DETAILED_ANALYSIS:
      return {
        ...state,
        isLoading: {
          ...state.isLoading,
          deatiledAnalysis: action.payload,
        },
      };

    case CLEAR_DETAILED_ANALYSIS:
      return {
        ...state,
        detailedAnalysis: initialState.detailedAnalysis,
        selectedBranch: initialState.selectedBranch,
        selectedGraphItem: initialState.selectedGraphItem,
        selectedEmail: initialState.selectedEmail,
        graphItems: initialState.graphItems,
      };

    case SET_CURRENT_THREAD:
      return {
        ...state,
        selectedThread: action.payload,
      };

    case SET_CURRENT_BRANCH:
      return {
        ...state,
        selectedBranch: action.payload,
      };

    case SET_CURRENT_EMAIL:
      return {
        ...state,
        selectedEmail: state.selectedBranch?.emails
          ? state.selectedBranch.emails.find(
              (email) => email?.platformMessageId === action.payload
            )
          : null,
      };

    case UPDATE_CURRENT_EMAIL:
      return {
        ...state,
        selectedEmail: action.payload,
      };

    case SET_GRAPH_ITEMS:
      return {
        ...state,
        graphItems: action.payload,
      };

    case SET_SELECTED_GRAPH_ITEM:
      return {
        ...state,
        selectedGraphItem: state?.graphItems?.length
          ? state.graphItems.find((item) => item.id === action.payload)
          : null,
      };

    /* eslint-disable */
    case UPDATE_CRM_DATA:
      return {
        ...state,
        detailedAnalysis: {
          ...state.detailedAnalysis!,
          deal: {
            ...state.detailedAnalysis!.deal,
            dealName:
              typeof action.payload?.dealName === 'string'
                ? action.payload?.dealName
                : state.detailedAnalysis!.deal.dealName,
            crmStage:
              typeof action.payload?.crmStage === 'string'
                ? action.payload?.crmStage
                : state.detailedAnalysis!.deal.crmStage,
            dealValue:
              typeof action.payload?.dealValue === 'string'
                ? action.payload?.dealValue
                : state.detailedAnalysis!.deal.dealValue,
          },
        },
      };

    /* eslint-enable */
    default:
      return state;
  }
};
